/**
 * Offline-Aware API Wrapper
 * Wraps the gaushala API to provide offline support
 *
 * Features:
 * - Caches entity data for offline access
 * - Queues write operations when offline
 * - Auto-syncs when coming back online
 * - Merges cached and pending data in list views
 */

import { offlineDb } from './db';
import {
  cacheEntity,
  cacheEntities,
  getCachedEntity,
  getCachedEntities,
  getPendingEntities,
  removeCachedEntity,
  updateSyncMetadata,
  needsRefresh,
} from './offlineStorage';
import { queueOperation, generateTempId, isTempId, QueueOperationParams } from './syncQueue';
import { EntityType, OFFLINE_CONFIG } from './constants';

// Get gaushalaId from JWT token
function getGaushalaIdFromToken(): number {
  try {
    const token = localStorage.getItem('saubhagya_jwt_token');
    if (!token) return 0;

    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.gaushalaId || 0;
  } catch (error) {
    console.error('[OfflineApi] Failed to get gaushalaId from token:', error);
    return 0;
  }
}

// API base URL configuration
const isDevelopment = import.meta.env.DEV;
const API_BASE_URL = isDevelopment
  ? '/gaushala-service'
  : (import.meta.env.VITE_GAUSHALA_SERVICE_URL || 'http://localhost:8086/gaushala-service');

/**
 * Generic API call helper with optional base URL override
 */
async function apiCall<T>(
  endpoint: string,
  options: RequestInit = {},
  baseUrl: string = API_BASE_URL
): Promise<{ success: boolean; data?: T; error?: string }> {
  try {
    const token = localStorage.getItem('saubhagya_jwt_token');
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    };

    const response = await fetch(`${baseUrl}${endpoint}`, {
      ...options,
      headers,
    });

    const contentType = response.headers.get('content-type');
    let data: any = null;

    if (contentType && contentType.includes('application/json')) {
      const text = await response.text();
      if (text) {
        data = JSON.parse(text);
      }
    }

    if (!response.ok) {
      return {
        success: false,
        error: data?.message || data?.error || `HTTP ${response.status}: ${response.statusText}`,
      };
    }

    return { success: true, data };
  } catch (error) {
    console.error(`[OfflineApi] API call failed:`, error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error',
    };
  }
}

// ============== GENERIC OFFLINE ENTITY OPERATIONS ==============

interface OfflineEntityConfig<T> {
  entityType: EntityType;
  listEndpoint: string;
  getEndpoint: (id: number | string) => string;
  createEndpoint: string;
  updateEndpoint: (id: number | string) => string;
  deleteEndpoint: (id: number | string) => string;
  baseUrl?: string; // Optional override for service base URL
}

/**
 * Create offline-aware CRUD operations for an entity type
 */
function createOfflineEntityApi<T extends { id?: number | string }>(config: OfflineEntityConfig<T>) {
  const { entityType, listEndpoint, getEndpoint, createEndpoint, updateEndpoint, deleteEndpoint, baseUrl } = config;
  const serviceBaseUrl = baseUrl || API_BASE_URL;

  return {
    /**
     * Get all entities (online: fetch + cache, offline: return cached)
     */
    async getAll(page: number = 0, size: number = 100): Promise<T[]> {
      const gaushalaId = getGaushalaIdFromToken();
      const isOnline = navigator.onLine;

      // If online, try to fetch from server
      if (isOnline) {
        try {
          const response = await apiCall<{ content?: T[]; data?: T[] } | T[]>(
            `${listEndpoint}?page=${page}&size=${size}`,
            {},
            serviceBaseUrl
          );

          if (response.success && response.data) {
            // Handle paged or array response
            const entities = Array.isArray(response.data)
              ? response.data
              : (response.data.content || response.data.data || []);

            // Cache all entities
            if (entities.length > 0) {
              await cacheEntities(entityType, entities as (T & { id: number | string })[], gaushalaId);
              await updateSyncMetadata(gaushalaId, entityType, entities.length);
            }

            console.log(`[OfflineApi] Fetched ${entities.length} ${entityType} from server`);
            return entities;
          }
        } catch (error) {
          console.error(`[OfflineApi] Failed to fetch ${entityType}:`, error);
          // Fall through to cache
        }
      }

      // Return cached data (including pending items)
      console.log(`[OfflineApi] Returning cached ${entityType}`);
      const cached = await getCachedEntities<T>(entityType, gaushalaId);
      const pending = await getPendingEntities<T>(entityType, gaushalaId);

      // Merge and deduplicate
      const merged = [...cached];
      for (const p of pending) {
        if (p.id && !merged.some(c => c.id === p.id)) {
          merged.push(p);
        }
      }

      return merged;
    },

    /**
     * Get single entity by ID
     */
    async getById(id: number | string): Promise<T | null> {
      const gaushalaId = getGaushalaIdFromToken();
      const isOnline = navigator.onLine;

      // If it's a temp ID, can only be in cache
      if (isTempId(String(id))) {
        return getCachedEntity<T>(entityType, id);
      }

      // If online, try to fetch from server
      if (isOnline) {
        try {
          const response = await apiCall<T>(getEndpoint(id), {}, serviceBaseUrl);

          if (response.success && response.data) {
            // Cache the entity
            await cacheEntity(entityType, response.data as T & { id: number | string }, gaushalaId);
            return response.data;
          }
        } catch (error) {
          console.error(`[OfflineApi] Failed to fetch ${entityType} ${id}:`, error);
          // Fall through to cache
        }
      }

      // Return from cache
      return getCachedEntity<T>(entityType, id);
    },

    /**
     * Create entity (online: create on server, offline: queue + cache)
     */
    async create(data: Omit<T, 'id' | 'createdAt' | 'updatedAt'>): Promise<{
      success: boolean;
      data?: T;
      error?: string;
      isOffline?: boolean;
      tempId?: string;
    }> {
      const gaushalaId = getGaushalaIdFromToken();
      const isOnline = navigator.onLine;

      // If online, try to create on server
      if (isOnline) {
        try {
          const response = await apiCall<T>(createEndpoint, {
            method: 'POST',
            body: JSON.stringify(data),
          }, serviceBaseUrl);

          if (response.success && response.data) {
            // Cache the created entity
            await cacheEntity(entityType, response.data as T & { id: number | string }, gaushalaId);
            return { success: true, data: response.data };
          }

          // API returned error (server down, 500, etc.) - fall through to offline queue
          console.log(`[OfflineApi] Server returned error for ${entityType} create - falling back to offline queue`);
        } catch (error) {
          console.error(`[OfflineApi] Failed to create ${entityType}:`, error);
          // Fall through to offline queue
        }
      }

      // Offline: generate temp ID and queue operation
      const tempId = generateTempId();
      const entityWithTempId = { ...data, id: tempId } as T & { id: string };

      // Cache with temp ID
      await cacheEntity(entityType, entityWithTempId, gaushalaId, true);

      // Queue for sync
      await queueOperation({
        entityType,
        operationType: 'CREATE',
        tempId,
        data: data as Record<string, any>,
        gaushalaId,
      });

      console.log(`[OfflineApi] Queued ${entityType} creation with temp ID: ${tempId}`);

      return {
        success: true,
        data: entityWithTempId,
        isOffline: true,
        tempId,
      };
    },

    /**
     * Update entity (online: update on server, offline: queue + update cache)
     */
    async update(
      id: number | string,
      data: Partial<T>
    ): Promise<{
      success: boolean;
      data?: T;
      error?: string;
      isOffline?: boolean;
    }> {
      const gaushalaId = getGaushalaIdFromToken();
      const isOnline = navigator.onLine;

      // If it's a temp ID, just update the queued operation
      if (isTempId(String(id))) {
        // Update the cached entity
        const existing = await getCachedEntity<T>(entityType, id);
        if (existing) {
          const updated = { ...existing, ...data };
          await cacheEntity(entityType, updated as T & { id: string }, gaushalaId, true);
        }

        // Update the queued CREATE operation data
        const operation = await offlineDb.pendingOperations.where('tempId').equals(String(id)).first();
        if (operation) {
          await offlineDb.pendingOperations.update(operation.id, {
            data: { ...operation.data, ...data },
          });
        }

        return {
          success: true,
          data: { ...existing, ...data } as T,
          isOffline: true,
        };
      }

      // If online, try to update on server
      if (isOnline) {
        try {
          const response = await apiCall<T>(updateEndpoint(id), {
            method: 'PUT',
            body: JSON.stringify(data),
          }, serviceBaseUrl);

          if (response.success && response.data) {
            // Update cache
            await cacheEntity(entityType, response.data as T & { id: number | string }, gaushalaId);
            return { success: true, data: response.data };
          }

          return { success: false, error: response.error };
        } catch (error) {
          console.error(`[OfflineApi] Failed to update ${entityType} ${id}:`, error);
          // Fall through to offline queue
        }
      }

      // Offline: update cache and queue operation
      const existing = await getCachedEntity<T>(entityType, id);
      const updated = { ...existing, ...data, id } as T & { id: number | string };

      await cacheEntity(entityType, updated, gaushalaId);

      await queueOperation({
        entityType,
        operationType: 'UPDATE',
        serverId: id as number,
        data: data as Record<string, any>,
        gaushalaId,
      });

      console.log(`[OfflineApi] Queued ${entityType} update for ID: ${id}`);

      return {
        success: true,
        data: updated,
        isOffline: true,
      };
    },

    /**
     * Delete entity (online: delete on server, offline: queue + mark in cache)
     */
    async delete(id: number | string): Promise<{
      success: boolean;
      error?: string;
      isOffline?: boolean;
    }> {
      const gaushalaId = getGaushalaIdFromToken();
      const isOnline = navigator.onLine;

      // If it's a temp ID, just remove from cache and queue
      if (isTempId(String(id))) {
        await removeCachedEntity(entityType, id);

        // Remove the queued CREATE operation
        const operation = await offlineDb.pendingOperations.where('tempId').equals(String(id)).first();
        if (operation) {
          await offlineDb.pendingOperations.delete(operation.id);
        }

        return { success: true, isOffline: true };
      }

      // If online, try to delete on server
      if (isOnline) {
        try {
          const response = await apiCall<void>(deleteEndpoint(id), {
            method: 'DELETE',
          }, serviceBaseUrl);

          if (response.success) {
            // Remove from cache
            await removeCachedEntity(entityType, id);
            return { success: true };
          }

          return { success: false, error: response.error };
        } catch (error) {
          console.error(`[OfflineApi] Failed to delete ${entityType} ${id}:`, error);
          // Fall through to offline queue
        }
      }

      // Offline: mark as deleted in cache and queue operation
      await removeCachedEntity(entityType, id);

      await queueOperation({
        entityType,
        operationType: 'DELETE',
        serverId: id as number,
        data: {},
        gaushalaId,
      });

      console.log(`[OfflineApi] Queued ${entityType} delete for ID: ${id}`);

      return { success: true, isOffline: true };
    },

    /**
     * Check if data needs refresh from server
     */
    async needsRefresh(): Promise<boolean> {
      const gaushalaId = getGaushalaIdFromToken();
      return needsRefresh(gaushalaId, entityType);
    },
  };
}

// ============== ENTITY-SPECIFIC OFFLINE APIs ==============

/**
 * Offline-aware Cattle API
 */
export const offlineCattleApi = createOfflineEntityApi({
  entityType: 'cattle',
  listEndpoint: '/api/v1/gaushala/cattle',
  getEndpoint: (id) => `/api/v1/gaushala/cattle/${id}`,
  createEndpoint: '/api/v1/gaushala/cattle',
  updateEndpoint: (id) => `/api/v1/gaushala/cattle/${id}`,
  deleteEndpoint: (id) => `/api/v1/gaushala/cattle/${id}`,
});

/**
 * Offline-aware Health Record API
 */
export const offlineHealthRecordApi = createOfflineEntityApi({
  entityType: 'healthRecord',
  listEndpoint: '/api/v1/gaushala/health-records',
  getEndpoint: (id) => `/api/v1/gaushala/health-records/${id}`,
  createEndpoint: '/api/v1/gaushala/health-records',
  updateEndpoint: (id) => `/api/v1/gaushala/health-records/${id}`,
  deleteEndpoint: (id) => `/api/v1/gaushala/health-records/${id}`,
});

/**
 * Offline-aware Milk Production API
 */
export const offlineMilkProductionApi = createOfflineEntityApi({
  entityType: 'milkProduction',
  listEndpoint: '/api/v1/gaushala/milk-records',
  getEndpoint: (id) => `/api/v1/gaushala/milk-records/${id}`,
  createEndpoint: '/api/v1/gaushala/milk-records',
  updateEndpoint: (id) => `/api/v1/gaushala/milk-records/${id}`,
  deleteEndpoint: (id) => `/api/v1/gaushala/milk-records/${id}`,
});

/**
 * Offline-aware Food History API
 */
export const offlineFoodHistoryApi = createOfflineEntityApi({
  entityType: 'foodHistory',
  listEndpoint: '/api/v1/gaushala/food-history',
  getEndpoint: (id) => `/api/v1/gaushala/food-history/${id}`,
  createEndpoint: '/api/v1/gaushala/food-history',
  updateEndpoint: (id) => `/api/v1/gaushala/food-history/${id}`,
  deleteEndpoint: (id) => `/api/v1/gaushala/food-history/${id}`,
});

/**
 * Offline-aware Medicine API
 */
export const offlineMedicineApi = createOfflineEntityApi({
  entityType: 'medicine',
  listEndpoint: '/api/v1/gaushala/medicines',
  getEndpoint: (id) => `/api/v1/gaushala/medicines/${id}`,
  createEndpoint: '/api/v1/gaushala/medicines',
  updateEndpoint: (id) => `/api/v1/gaushala/medicines/${id}`,
  deleteEndpoint: (id) => `/api/v1/gaushala/medicines/${id}`,
});

/**
 * Offline-aware Inventory API
 */
export const offlineInventoryApi = createOfflineEntityApi({
  entityType: 'inventory',
  listEndpoint: '/api/v1/gaushala/inventory',
  getEndpoint: (id) => `/api/v1/gaushala/inventory/${id}`,
  createEndpoint: '/api/v1/gaushala/inventory',
  updateEndpoint: (id) => `/api/v1/gaushala/inventory/${id}`,
  deleteEndpoint: (id) => `/api/v1/gaushala/inventory/${id}`,
});

/**
 * Offline-aware Shed API
 */
export const offlineShedApi = createOfflineEntityApi({
  entityType: 'shed',
  listEndpoint: '/api/v1/gaushala/sheds',
  getEndpoint: (id) => `/api/v1/gaushala/sheds/${id}`,
  createEndpoint: '/api/v1/gaushala/sheds',
  updateEndpoint: (id) => `/api/v1/gaushala/sheds/${id}`,
  deleteEndpoint: (id) => `/api/v1/gaushala/sheds/${id}`,
});

/**
 * Offline-aware Dung Transaction API
 * Fetches raw material transactions from gaushala service
 */
export const offlineDungTransactionApi = createOfflineEntityApi({
  entityType: 'dungTransaction',
  listEndpoint: '/api/v1/raw-material-transactions',
  getEndpoint: (id) => `/api/v1/raw-material-transactions/${id}`,
  createEndpoint: '/api/v1/raw-material-transactions',
  updateEndpoint: (id) => `/api/v1/raw-material-transactions/${id}`,
  deleteEndpoint: (id) => `/api/v1/raw-material-transactions/${id}`,
});

/**
 * Offline-aware RFID Scan API
 */
export const offlineRfidScanApi = createOfflineEntityApi({
  entityType: 'rfidScan',
  listEndpoint: '/api/v1/gaushala/rfid-scans',
  getEndpoint: (id) => `/api/v1/gaushala/rfid-scans/${id}`,
  createEndpoint: '/api/v1/gaushala/rfid-scans',
  updateEndpoint: (id) => `/api/v1/gaushala/rfid-scans/${id}`,
  deleteEndpoint: (id) => `/api/v1/gaushala/rfid-scans/${id}`,
});

// ============== UNIFIED OFFLINE API ==============

export const offlineGaushalaApi = {
  cattle: offlineCattleApi,
  healthRecord: offlineHealthRecordApi,
  milkProduction: offlineMilkProductionApi,
  foodHistory: offlineFoodHistoryApi,
  medicine: offlineMedicineApi,
  inventory: offlineInventoryApi,
  shed: offlineShedApi,
  dungTransaction: offlineDungTransactionApi,
  rfidScan: offlineRfidScanApi,
};

export default offlineGaushalaApi;
